import React from 'react';
import { useTranslation } from 'react-i18next';

import classNames from 'classnames';
import { Divider, ProgramIcon, ScrollBox, useAlert } from 'fasoo-ui-component-next';
import _ from 'lodash';

import approvalDetailApi, {
  ApprovalDetailEntity,
  ApprovalStatusType,
  RequestApprovalDto,
} from 'src/api/user/approvalApi';
import { ScreenApprovalDetailEntity } from 'src/api/user/screenApprovalApi';
import { CommonProps } from 'src/components/helpers/props';
import usePopup from 'src/hooks/usePopup';
import { getFasooUiComponentLanguage } from 'src/i18n';
import { toLocalDateTimeLangStr } from 'src/shared/utils/localDateTime';

import { ApprovalDetailFooter } from '../ApprovalDetailFooter';

import { ApprovalTypeIconArea } from './ApprovalTypeIconArea';
import RemarkPopup from './RemarkPopup';
import { ApprovalUserInfoDto, ShowLineContent, UserDetail } from '.';

import './ExceptScreenDetail.scss';

interface ExceptScreenDetailProps extends CommonProps {
  screenApproval: ScreenApprovalDetailEntity;
  ResetDetail: () => void;
  cancelApprovalChildren?: React.ReactNode;
  onCloseDetail: (open: boolean, isReject?: boolean) => void;
  isComplete?: boolean;
  selectedApproval?: ApprovalDetailEntity | null;
}

const ExceptScreenDetail: React.FC<ExceptScreenDetailProps> = ({
  screenApproval,
  ResetDetail,
  cancelApprovalChildren,
  onCloseDetail,
  isComplete = false,
  selectedApproval,
}) => {
  const { t } = useTranslation(['menu', 'userApproval', 'userRequest']);
  const { alert } = useAlert();
  const openApprovalPopup = usePopup();
  const openRejectPopup = usePopup();

  let approvers: ApprovalUserInfoDto[] = [];
  if (screenApproval.completeApprovers.length > 0) {
    const completes = _.sortBy(screenApproval.completeApprovers, 'step');
    approvers = approvers.concat(
      completes.map((approver) => {
        return {
          step: approver.step,
          approvalStatus: approver.approvalStatus,
          userId: approver.userId,
          userName: approver.userName,
          positionName: approver.positionName,
          deptName: approver.deptName,
          actorIp: approver.actorIp,
          approvalRemark: approver.approvalRemark,
          approvalDate: approver.updatedTs,
          method: approver.approvalMethod,
          otherDeptCode: approver.otherDeptCode === null ? null : approver.otherDeptCode,
          otherDeptName: approver.otherDeptName === null ? null : approver.otherDeptName,
          otherPositionCode:
            approver.otherPositionCode === null ? null : approver.otherPositionCode,
          otherPositionName:
            approver.otherPositionName === null ? null : approver.otherPositionName,
          otherRoleCode: approver.otherRoleCode === null ? null : approver.otherRoleCode,
          otherRoleName: approver.otherRoleName === null ? null : approver.otherRoleName,
          otherUserId: approver.otherUserId === null ? null : approver.otherUserId,
          otherUserName: approver.otherUserName === null ? null : approver.otherUserName,
        };
      })
    );
  }
  if (screenApproval.progressApprovers.length > 0) {
    const progresses = _.sortBy(screenApproval.progressApprovers, 'step');
    approvers = approvers.concat(
      progresses.map((approver) => {
        return {
          step: approver.step,
          approvalStatus: ApprovalStatusType.PROGRESS,
          userId: approver.userId,
          userName: approver.userName,
          positionName: approver.positionName,
          deptName: approver.deptName,
        };
      })
    );
  }
  // 결재선이 다 끝나지 않는 단계
  // if (screenApproval.currentStep < screenApproval.approvalStep) {
  //   let postApprovalSteps: number[] = [];
  //   for (let i = screenApproval.currentStep + 1; i <= screenApproval.approvalStep; i++) {
  //     postApprovalSteps = postApprovalSteps.concat(i);
  //   }

  approvers = approvers.concat(
    screenApproval.approvers
      .filter(
        (approver) =>
          // postApprovalSteps.includes(approver.step) &&
          approvers.filter((v) => v.step === approver.step)?.length === 0
      )
      .map((approver) => {
        return {
          step: approver.step,
          approvalStatus: ApprovalStatusType.PROGRESS,
          userId: approver.userId,
          userName: approver.userName,
          positionName: approver.positionName,
          deptName: approver.deptName,
        };
      })
  );
  // }

  const handleOpenApprovalPopup = (type: string): void => {
    if (type === 'approval') {
      openApprovalPopup.openPopup();
    } else if (type === 'reject') {
      openRejectPopup.openPopup();
    }
  };

  const handleCloseApprovalPopup = (type: string): void => {
    if (type === 'approval') {
      openApprovalPopup.closePopup();
    } else if (type === 'reject') {
      openRejectPopup.closePopup();
    }
  };

  const handleApprovalReject = (remark: string): void => {
    const popupType = openApprovalPopup.opened !== false ? 'approval' : 'reject';

    // console.log('얘가 작성한 remark : ', remark);
    if (screenApproval.progressApprovers.length > 0) {
      const progressApproverInfo = screenApproval.progressApprovers[0];
      const reqApprovalDto: RequestApprovalDto = {
        userId: progressApproverInfo.userId,
        approvalNo: screenApproval.approvalNo,
        approvalMethod: screenApproval.approvalMethod,
        approvalType: screenApproval.approvalType,
        status: popupType === 'approval' ? 'APPROVAL' : 'REJECT',
        remark,
        proxyNo: selectedApproval?.progressApprovers[0].proxyNo
          ? selectedApproval.progressApprovers[0].proxyNo
          : null,
      };
      approvalDetailApi
        .approvalOrReject(reqApprovalDto)
        .then(() => {
          onCloseDetail(false, true);
        })
        .catch((err) => alert(err, 'error', '', undefined, true));
    } else {
      onCloseDetail(false);
    }

    handleCloseApprovalPopup(popupType);
  };

  return (
    <div className="except-screen-detail-root">
      <div
        className={classNames('except-screen-detail-maincontainer', {
          'mg mb-85': isComplete === false,
          // 'mg mb-32': isComplete === true,
        })}
      >
        {/* 신청 유형 아이콘 area */}

        <ApprovalTypeIconArea
          onClose={onCloseDetail}
          type={screenApproval.approvalType}
          status={screenApproval.status}
        />
        {/* 대상 프로그램 */}
        {screenApproval.processes.length > 0 ? (
          <ShowLineContent
            title={
              <>
                <span>{t('userRequest:targetProgram')}</span>
                <span className="process-count">{screenApproval.processes.length}</span>
              </>
            }
            content={
              <ScrollBox classes="process-root" type="transparent" maxHeight={318}>
                {screenApproval.processes.map((process) => (
                  <div className="target-process-item line-height-initail">
                    <ProgramIcon outLine={false} />
                    {process.value}
                  </div>
                ))}
              </ScrollBox>
            }
          />
        ) : (
          <ShowLineContent
            title={<span>{t('userRequest:targetProgram')}</span>}
            content={
              <ScrollBox classes="process-root" type="transparent" maxHeight={318}>
                <div className="target-process-item line-height-initail">
                  {t('userRequest:allPrograms')}
                </div>
              </ScrollBox>
            }
          />
        )}

        {/* 적용 범위 */}
        <ShowLineContent
          title={t('userRequest:targetRange.$')}
          content={screenApproval.exceptTargetList
            .map((process) => t(`userRequest:targetRange.${process}`))
            .join('/')}
        />

        {/* 권한 적용 기간 */}
        <ShowLineContent
          title={t('userApproval:exceptScreenDate')}
          content={`${toLocalDateTimeLangStr(
            screenApproval.startDate,
            getFasooUiComponentLanguage()
          )} ~ ${toLocalDateTimeLangStr(screenApproval.endDate, getFasooUiComponentLanguage())}`}
        />

        {/* 승인 방법 */}
        <ShowLineContent
          classes={classNames('approval-method')}
          title={t('userApproval:approvalMethod.$')}
          content={t(`userApproval:approvalMethod.${screenApproval.approvalMethod}`)}
        />

        {/* 신청자 정보 */}
        <ShowLineContent
          title={t('userApproval:applicant')}
          content={
            <UserDetail
              type="request"
              dateStr={toLocalDateTimeLangStr(
                screenApproval.applyDate,
                getFasooUiComponentLanguage()
              )}
              remark={screenApproval.remark}
              userInfo={{
                userId: screenApproval.userId,
                userName: screenApproval.userName,
                positionName: screenApproval.positionName,
                deptName: screenApproval.deptName,
                actorIp: screenApproval.actorIp,
              }}
            />
          }
        />

        {/* 결재 상세 정보 */}
        <div className="approval-detail-info-title">
          <div className="title-text">{t('userApproval:approvalDetail.$')}</div>
        </div>
        <ShowLineContent
          title=""
          content={
            <>
              {approvers.map((approver, idx) => {
                return (
                  <UserDetail
                    step={approvers.length > 1 ? approver.step : undefined}
                    type="approver"
                    dateStr={
                      approver.approvalDate
                        ? toLocalDateTimeLangStr(
                            approver.approvalDate,
                            getFasooUiComponentLanguage()
                          )
                        : null
                    }
                    remark={approver.approvalRemark ? approver.approvalRemark : null}
                    userInfo={approver}
                    key={`approver-${idx}`}
                  />
                );
              })}

              {approvers.length <= 1 || <Divider />}
            </>
          }
        />

        {cancelApprovalChildren !== undefined && cancelApprovalChildren}
      </div>
      {isComplete === false && (
        <ApprovalDetailFooter
          approvalMethod={screenApproval.approvalMethod}
          handleApprove={() => handleOpenApprovalPopup('approval')}
          handleReject={() => handleOpenApprovalPopup('reject')}
        />
      )}
      {(openApprovalPopup.opened === true || openRejectPopup.opened === true) && (
        <RemarkPopup
          open={
            openApprovalPopup.opened === true ? openApprovalPopup.opened : openRejectPopup.opened
          }
          entity={screenApproval}
          onSubmit={handleApprovalReject}
          onClose={handleCloseApprovalPopup}
          isReject={openApprovalPopup.opened !== true}
        />
      )}
    </div>
  );
};

export default ExceptScreenDetail;
